import { computed } from 'vue'

import type { Deal } from '@backmarket/nuxt-layer-cms/products/algolia/types'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { IconArrowDownRightInCircleFilled } from '@ds/icons/IconArrowDownRightInCircleFilled'
import { IconBoltFilled } from '@ds/icons/IconBoltFilled'
import { IconPriceTagFilled } from '@ds/icons/IconPriceTagFilled'

import type { Granularity } from '../models/product'

import { selectEligibleDeal } from './selectEligibleDeal'
import translations from './useProductCardDeal.translations'

export function useProductCardDeal(
  deals?: Deal[],
  granularity?: Granularity,
  withDeal = true,
  excludedDealTypes: Deal['type'][] = [],
) {
  const i18n = useI18n()
  const experiments = useExperiments()

  const eligibleDeal = computed(() =>
    selectEligibleDeal(
      deals,
      granularity,
      withDeal,
      excludedDealTypes,
      experiments,
    ),
  )

  const dealTag = computed(() => {
    if (!eligibleDeal.value) return undefined
    if (eligibleDeal.value.type === 'manual_promo_code') {
      return {
        label:
          granularity === 'parent'
            ? i18n(translations.manualPromoCodeAvailable)
            : i18n(translations.manualPromoCode, {
                discount_value: eligibleDeal.value.percentage_value,
                promo_code: eligibleDeal.value.discount_code,
              }),
        icon: IconPriceTagFilled,
        tone: 'neutral' as const,
      }
    }

    if (eligibleDeal.value.type === 'flash_sales') {
      return {
        label: i18n(translations.flashSalesTag),
        icon: IconBoltFilled,
      }
    }

    if (
      eligibleDeal.value.type === 'seller_flash_sales' &&
      eligibleDeal.value.percentage
    ) {
      return {
        label: i18n(translations.sellerFlashSalesTag),
        icon: IconArrowDownRightInCircleFilled,
      }
    }

    return undefined
  })

  return {
    eligibleDeal,
    dealTag,
  }
}
